import { useRef } from "react";
import { useTranslation } from "react-i18next";
import { IconAlertCircle } from "@tabler/icons-react";

import { Text } from "@fragments";
import AICircle from "@components/AICircle";

import { CHAT_WIDTH, PREVIEW_WIDTH } from "../enums/layout";
import { STATUS } from "../hooks/useGenerateVideoScript";
import ScriptComposer from "./components/ScriptComposer";
import ChatThread from "./components/ChatThread";
import ScriptPreview from "./components/ScriptPreview";

import "./styles.scss";

/**
 * Two columns: the conversation and, once a script is opened, the preview
 * beside it behind a hairline. The preview column animates its width in
 * step with the modal's, so the chat column never changes size while the
 * surface grows.
 *
 * In the chat column only the composer is pinned: the hero scrolls away
 * with the thread, dissolving under a blurred veil at the top edge.
 */
export default function GenerateVideoScriptView({
    contentName,
    prompt,
    promptError,
    duration,
    durations,
    personality,
    personalities,
    status,
    messages,
    previewMessage,
    previewOpen,
    contextLine,
    steps,
    stepIndex,
    onPromptChange,
    onDurationChange,
    onPersonalityChange,
    onGenerate,
    onRetry,
    onOpenPreview,
    onClosePreview,
    onCopy,
    onDownloadPdf,
    onDownloadDocx,
}) {
    const { t } = useTranslation("contentModals");
    const scrollRef = useRef(null);

    const generating = status === STATUS.GENERATING;
    const chat = messages.length > 0;

    const className = [
        "generate-script",
        chat ? "is-chat" : "",
        previewOpen ? "is-preview-open" : "",
    ].filter(Boolean).join(" ");

    return (
        <div
            className={className}
            style={{ "--gs-chat-width": `${CHAT_WIDTH}px`, "--gs-preview-width": `${PREVIEW_WIDTH}px` }}
        >
            <section className="generate-script__chat">
                <div ref={scrollRef} className="generate-script__scroll">
                    <header className="generate-script__hero">
                        <div className="generate-script__orb" aria-hidden="true">
                            <AICircle size={56} />
                        </div>
                        <Text.h4 weight={500} contrast={9}>{t("generateScript.title")}</Text.h4>
                        <Text.span contrast={6} className="generate-script__subtitle">
                            {t("generateScript.subtitle", { name: contentName })}
                        </Text.span>
                    </header>

                    {chat && (
                        <ChatThread
                            messages={messages}
                            steps={steps}
                            stepIndex={stepIndex}
                            previewId={previewOpen ? previewMessage?.id : null}
                            scrollRef={scrollRef}
                            onOpenPreview={onOpenPreview}
                            onRetry={onRetry}
                        />
                    )}
                </div>

                <div className="generate-script__veil" aria-hidden="true" />

                <div className="generate-script__composer">
                    <ScriptComposer
                        value={prompt}
                        onChange={onPromptChange}
                        duration={duration}
                        durations={durations}
                        onDurationChange={onDurationChange}
                        personality={personality}
                        personalities={personalities}
                        onPersonalityChange={onPersonalityChange}
                        generating={generating}
                        followUp={chat}
                        onGenerate={onGenerate}
                    />

                    {promptError && (
                        <Text.caption className="generate-script__hint" left={<IconAlertCircle />} role="alert">
                            {promptError}
                        </Text.caption>
                    )}
                </div>
            </section>

            <aside
                className={`generate-script__preview ${previewOpen ? "is-open" : ""}`.trim()}
                aria-hidden={!previewOpen}
                inert={previewOpen ? undefined : ""}
            >
                <div className="generate-script__preview-inner">
                    {previewMessage?.script && (
                        <ScriptPreview
                            key={previewMessage.id}
                            script={previewMessage.script}
                            version={previewMessage.version}
                            contextLine={contextLine}
                            onClose={onClosePreview}
                            onCopy={onCopy}
                            onDownloadPdf={onDownloadPdf}
                            onDownloadDocx={onDownloadDocx}
                        />
                    )}
                </div>
            </aside>
        </div>
    );
}
